import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Drag-and-drop navigation editor.
//
// Two flat SortableJS instances:
//   • Top-level list (data-nav-editor-target="topLevel") — orders
//     top-level nav items.
//   • Per-parent child lists (data-nav-editor-target="children") — each
//     declares `data-accepts="page"` or `data-accepts="program"` so
//     `onMove` can reject mismatched drops up-front (you can't drop a
//     page under Programs, you can't drop a program under About).
//
// On every drop we walk the DOM, build a flat tree
// [{slug, parent_slug, position}] and POST it. Server is also strict
// about validation (sees through any client tampering).
//
// Eyeball-toggle on each program child fires a focused POST to
// /toggle_program, which flips Program.nav_visible without a tree save.
export default class extends Controller {
  static targets = ["topLevel", "children", "status"];
  static values  = { saveUrl: String, toggleProgramUrl: String, togglePageUrl: String, csrf: String };

  connect() {
    this.initSortable(this.topLevelTarget, "any");
    this.childrenTargets.forEach(zone => {
      this.initSortable(zone, zone.dataset.accepts);
    });
    this.setStatus("Saved", "ok");
  }

  initSortable(el, acceptKind) {
    Sortable.create(el, {
      group: "nav",
      animation: 150,
      handle: "[data-nav-editor-handle]",
      ghostClass: "page-editor-ghost",
      chosenClass: "page-editor-chosen",
      // Reject mismatched drops up-front. Returning false from onMove
      // prevents the placeholder from appearing in the target zone, so
      // the user gets immediate visual feedback that the drop is invalid.
      onMove: (evt) => {
        const draggedKind = (evt.dragged.dataset.kind || "page");
        const accepts     = (evt.to.dataset.accepts || "any");
        if (accepts === "any") return true;
        return draggedKind === accepts;
      },
      onEnd: () => this.scheduleSave(),
    });
  }

  // ── Save (debounced) ──────────────────────────────────────────

  scheduleSave() {
    clearTimeout(this.saveTimer);
    this.setStatus("Saving…", "editing");
    this.saveTimer = setTimeout(() => this.save(), 400);
  }

  async save() {
    const tree = this.serialize();
    try {
      const fd = new FormData();
      fd.append("tree", JSON.stringify(tree));
      fd.append("authenticity_token", this.csrfValue);
      const res = await fetch(this.saveUrlValue, {
        method: "PATCH",
        body: fd,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (res.ok) this.setStatus("Saved · " + this.now(), "ok");
      else this.setStatus("Save failed (" + res.status + ")", "error");
    } catch (e) {
      this.setStatus("Save failed (network)", "error");
    }
  }

  // ── Toggle nav_visible (programs + pages) ────────────────────
  // Both kinds use the same UX (eye icon) and the same response shape
  // (`{ nav_visible: bool }`). The button's `data-toggle-kind` ("program"
  // or "page") picks the endpoint + payload key.

  async toggleProgram(event) { return this.toggleVisibility(event, "program"); }
  async togglePage(event)    { return this.toggleVisibility(event, "page"); }

  async toggleVisibility(event, kind) {
    event.preventDefault();
    event.stopPropagation();
    const btn = event.currentTarget;
    const row = btn.closest("[data-slug]");
    if (!row) return;
    btn.disabled = true;

    const url = kind === "program" ? this.toggleProgramUrlValue : this.togglePageUrlValue;
    const fd  = new FormData();
    fd.append("authenticity_token", this.csrfValue);
    if (kind === "program") {
      fd.append("program_id", btn.dataset.programId);
    } else {
      fd.append("slug", row.dataset.slug);
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (res.ok) {
        const data = await res.json();
        this.applyHiddenState(row, btn, data.nav_visible);
        this.setStatus("Saved · " + this.now(), "ok");
      } else {
        this.setStatus("Toggle failed", "error");
      }
    } finally {
      btn.disabled = false;
    }
  }

  // Centralized "this row is hidden from nav" treatment so the
  // toggle-handler doesn't have to know which classes/badges to flip.
  applyHiddenState(row, btn, navVisible) {
    row.classList.toggle("opacity-60", !navVisible);
    const badge = row.querySelector("[data-nav-editor-hidden-badge]");
    if (badge) badge.toggleAttribute("hidden", navVisible);
    btn.title = navVisible ? "Hide from nav" : "Show in nav";
    btn.innerHTML = navVisible ? eyeOpenSvg() : eyeOffSvg();
  }

  // ── Serialization ─────────────────────────────────────────────

  serialize() {
    const out = [];
    Array.from(this.topLevelTarget.children).forEach((node, i) => {
      const slug = node.dataset.slug;
      if (!slug) return;
      out.push({ slug, parent_slug: null, position: (i + 1) * 10 });
    });
    this.childrenTargets.forEach(zone => {
      const parent = zone.dataset.parentSlug;
      Array.from(zone.children).forEach((node, i) => {
        const slug = node.dataset.slug;
        if (!slug) return;
        out.push({ slug, parent_slug: parent, position: (i + 1) * 10 });
      });
    });
    return out;
  }

  setStatus(text, state) {
    if (!this.hasStatusTarget) return;
    const cls = {
      ok:      "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
      editing: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
      error:   "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    }[state] || "bg-gray-100 text-gray-700";
    this.statusTarget.className = "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full " + cls;
    this.statusTarget.textContent = text;
  }

  now() {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
}

// Eye SVGs as strings — kept module-level so the inline HTML in the
// template stays readable. Both are 1.6-stroke 24x24 to match the rest
// of the admin chrome.
function eyeOpenSvg() {
  return `<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}
function eyeOffSvg() {
  return `<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}
